import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { Sparkline } from '../systemKit';
import { fmtCompact, fmtSignedPct } from './utils';

// HeadlineStrip — three tiles at the very top of the Cost Dashboard.
// Each tile is its own narrow KPI: Spend (with sparkline), Anomaly count,
// Watchlist alerts. Click handlers are passed in by the parent so the
// tiles can scroll-to-section / open drill-ins / highlight rows.
//
// Each tile has its own loading skeleton — no all-or-nothing render.

export default function HeadlineStrip({
  companyId,
  startDate,
  endDate,
  watchlistAlertCount,
  onClickAnomalies,
  onClickWatchlist,
}) {
  const [spend, setSpend] = useState(null);
  const [anomaly, setAnomaly] = useState(null);

  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;
    let cancelled = false;
    setSpend(null);
    setAnomaly(null);
    (async () => {
      const [{ data: spendRows }, { data: anomalyRows }] = await Promise.all([
        supabase.rpc('cost_dashboard_spend_pace', {
          p_company_id: companyId,
          p_start_date: startDate,
          p_end_date: endDate,
        }),
        supabase.rpc('cost_dashboard_anomaly_summary', {
          p_company_id: companyId,
          p_start_date: startDate,
          p_end_date: endDate,
        }),
      ]);
      if (cancelled) return;
      setSpend(spendRows?.[0] || {});
      setAnomaly(anomalyRows?.[0] || {});
    })();
    return () => { cancelled = true; };
  }, [companyId, startDate, endDate]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <SpendTile data={spend} />
      <AnomalyTile data={anomaly} onClick={onClickAnomalies} />
      <WatchlistTile count={watchlistAlertCount} onClick={onClickWatchlist} />
    </div>
  );
}

// ─── Tiles ──────────────────────────────────────────────────────────────────

function TileShell({ children, onClick, className = '' }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`text-left ${className}`}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 16,
        minHeight: 88,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 120ms ease',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.borderColor = 'var(--border)'; } : undefined}
    >
      {children}
    </Tag>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--text-secondary)',
      marginBottom: 4,
    }}>{children}</div>
  );
}

function SpendTile({ data }) {
  if (!data) return <TileShell><Skeleton /></TileShell>;
  const cur = Number(data.current_spend || 0);
  const pctPrior = data.pct_change_vs_prior;
  const pctYr = data.pct_change_vs_year_ago;
  const sparkValues = (data.weekly_spend_points || []).map(p => Number(p.spend) || 0);

  return (
    <TileShell>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Eyebrow>Spend in window</Eyebrow>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {fmtCompact(cur)}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {pctPrior != null ? (
            <>
              vs prior{' '}
              <span style={{
                color: pctPrior > 0 ? 'var(--text-critical)' : pctPrior < 0 ? 'var(--text-success)' : 'var(--text-muted)',
                fontWeight: 600,
              }}>
                {fmtSignedPct(pctPrior)}
              </span>
            </>
          ) : <span style={{ color: 'var(--text-muted)' }}>no prior data</span>}
          {pctYr != null && (
            <>
              {' · vs last yr '}
              <span style={{
                color: pctYr > 0 ? 'var(--text-critical)' : pctYr < 0 ? 'var(--text-success)' : 'var(--text-muted)',
                fontWeight: 600,
              }}>{fmtSignedPct(pctYr)}</span>
            </>
          )}
        </div>
      </div>
      {sparkValues.length >= 2 && (
        <div style={{ flexShrink: 0 }}>
          <Sparkline data={sparkValues} width={96} height={36} tone="neutral" />
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            6 wk
          </div>
        </div>
      )}
    </TileShell>
  );
}

function AnomalyTile({ data, onClick }) {
  if (!data) return <TileShell><Skeleton /></TileShell>;
  const moved = Number(data.total_movers || 0);
  const unusual = Number(data.unusual_movers || 0);
  const critical = Number(data.critical_movers || 0);
  return (
    <TileShell onClick={onClick}>
      <div style={{ flex: 1 }}>
        <Eyebrow>Anomalies</Eyebrow>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
          {moved} <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>moved</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>
            <span style={{ color: 'var(--text-warning)', fontWeight: 600 }}>{unusual}</span> unusual
          </span>
          <span>
            <span style={{ color: 'var(--text-critical)', fontWeight: 600 }}>{critical}</span> critical
          </span>
          {Number(data.consecutive_increases || 0) > 0 && (
            <span>
              <span style={{ color: 'var(--text-critical)', fontWeight: 600 }}>{data.consecutive_increases}</span> consecutive
            </span>
          )}
        </div>
      </div>
    </TileShell>
  );
}

function WatchlistTile({ count, onClick }) {
  if (count == null) return <TileShell><Skeleton /></TileShell>;
  return (
    <TileShell onClick={onClick}>
      <div style={{ flex: 1 }}>
        <Eyebrow>Watchlist alerts</Eyebrow>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
          {count} <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>moved</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
          {count > 0 ? 'Scroll to watchlist →' : 'Quiet — nothing to flag'}
        </div>
      </div>
    </TileShell>
  );
}

function Skeleton() {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ height: 10, width: 80, background: 'var(--bg-subtle)', borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 24, width: 120, background: 'var(--bg-subtle)', borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 10, width: 160, background: 'var(--bg-subtle)', borderRadius: 4 }} />
    </div>
  );
}
